import Card, { CardContent } from '@cobliteam/ui/components/Card';
import type { Column, Table as ITable } from '@tanstack/react-table';
import type React from 'react';
import DataTableColumns from '../components/DataTableColumns';
import DataTableFilters from '../components/DataTableFilters';
import DataTableRefresh from '../components/DataTableRefresh';
import ExportComponent, { type ExportDef } from '../components/Export';
import type { IFiltersDef, TFilterKeys } from '../types';
import DataTableSearchInput from './DataTableSearchInput';

export interface DataTableToolbarProps<TData> {
  filtersOnSecondRow: boolean;
  search: string | false;
  searchValue: string;
  defaultPlaceholder: string;
  isFetching?: boolean;
  isLoading?: boolean;
  datePicker?: React.ReactNode;
  filtersDropdown?: React.ReactNode;
  showColumnFilter?: boolean;
  columns: Column<TData>[];
  visibleColumns: Column<TData>[];
  exportDef?: ExportDef<TData>;
  data?: TData[];
  table: ITable<TData>;
  filtersDef?: IFiltersDef[];
  filtersOptions?: { key: TFilterKeys; options: any }[];
  filtersLoading?: boolean;
  favoriteFilters: TFilterKeys[];
  live?: React.ReactNode;
  handleChangeSearch: (value: string) => void;
  onRefresh?: () => void;
  Export?: (table: ITable<TData>, data: TData[], exportDef?: ExportDef<TData>) => JSX.Element;
  onChangeFilter: (value: IFiltersDef) => void;
  onSelectedFiltersChange: (keys: TFilterKeys[]) => void;
}

type ToolbarActionsProps<TData> = Pick<DataTableToolbarProps<TData>, 'showColumnFilter' | 'columns' | 'visibleColumns' | 'exportDef' | 'Export' | 'data' | 'table'>;

function ToolbarActions<TData>({ showColumnFilter = true, columns, visibleColumns, exportDef, Export, data, table }: ToolbarActionsProps<TData>) {
  return (
    <div className='ml-auto flex shrink-0 items-center gap-2'>
      {showColumnFilter && <DataTableColumns columns={columns} visibleColumns={visibleColumns} />}
      {exportDef && Export && data ? Export(table, data, exportDef) : null}
      {!exportDef && Export && data ? Export(table, data, exportDef) : null}
      {exportDef && !Export && data ? <ExportComponent data={data} table={table} exportDef={exportDef} /> : null}
    </div>
  );
}

type ToolbarFiltersRowProps<TData> = Pick<
  DataTableToolbarProps<TData>,
  'filtersDef' | 'filtersOptions' | 'filtersLoading' | 'onChangeFilter' | 'onSelectedFiltersChange' | 'favoriteFilters'
> & { filtersOnSecondRow: boolean; wrapperClassName?: string; trailing?: React.ReactNode };

function ToolbarFiltersRow<TData>({
  filtersDef,
  filtersOptions,
  filtersLoading,
  onChangeFilter,
  onSelectedFiltersChange,
  favoriteFilters,
  filtersOnSecondRow,
  wrapperClassName,
  trailing,
}: ToolbarFiltersRowProps<TData>) {
  if (!filtersDef?.length) return null;
  return (
    <div className={wrapperClassName}>
      <DataTableFilters
        filtersDef={filtersDef}
        filtersOptions={filtersOptions}
        filtersLoading={filtersLoading}
        onChangeFilter={onChangeFilter}
        onSelectedFiltersChange={onSelectedFiltersChange}
        favoriteFilters={favoriteFilters}
        filtersOnSecondRow={filtersOnSecondRow}
        trailing={trailing}
      />
    </div>
  );
}

const searchInputProps = (value: string, placeholder: string, onChange: (v: string) => void) => ({
  value: value || '',
  placeholder,
  onChange,
});

function DataTableToolbar<TData>({
  filtersOnSecondRow: _filtersOnSecondRow,
  search,
  searchValue,
  defaultPlaceholder,
  handleChangeSearch,
  onRefresh,
  isFetching,
  isLoading,
  datePicker,
  filtersDropdown,
  showColumnFilter,
  columns,
  visibleColumns,
  exportDef,
  Export,
  data,
  table,
  filtersDef,
  filtersOptions,
  filtersLoading,
  onChangeFilter,
  onSelectedFiltersChange,
  favoriteFilters,
  live,
}: DataTableToolbarProps<TData>) {
  const hasFilters = !!filtersDef?.length;
  const searchProps = searchInputProps(searchValue, defaultPlaceholder, handleChangeSearch);

  return (
    <Card>
      <CardContent className='flex flex-col gap-2'>
        {/* Mobile / tablet: single row for controls, then filters */}
        <div className='flex laptop:hidden flex-col gap-2'>
          <div className='flex min-w-0 items-center gap-2'>
            <DataTableRefresh onRefresh={onRefresh} isFetching={isFetching || isLoading} />
            {search !== false && (
              <div className='min-w-0 flex-1'>
                <DataTableSearchInput {...searchProps} />
              </div>
            )}
            {datePicker && <div className='shrink-0'>{datePicker}</div>}
            <ToolbarActions showColumnFilter={showColumnFilter} columns={columns} visibleColumns={visibleColumns} exportDef={exportDef} Export={Export} data={data} table={table} />
          </div>
          {(hasFilters || filtersDropdown) && (
            <div className='flex items-center gap-4 overflow-hidden border-gray-6 border-t pt-2'>
              {hasFilters ? (
                <ToolbarFiltersRow
                  filtersDef={filtersDef}
                  filtersOptions={filtersOptions}
                  filtersLoading={filtersLoading}
                  onChangeFilter={onChangeFilter}
                  onSelectedFiltersChange={onSelectedFiltersChange}
                  favoriteFilters={favoriteFilters}
                  filtersOnSecondRow
                  wrapperClassName='flex flex-grow items-center gap-4 overflow-hidden'
                  trailing={filtersDropdown}
                />
              ) : (
                filtersDropdown && <div className='ml-auto'>{filtersDropdown}</div>
              )}
            </div>
          )}
        </div>

        {/* Laptop+: single row with filters after datepicker */}
        <div className='laptop:flex hidden flex-col gap-2'>
          <div className='flex items-center gap-2'>
            {live && <div className='shrink-0'>{live}</div>}
            <DataTableRefresh onRefresh={onRefresh} isFetching={isFetching || isLoading} />
            {search !== false && (
              <div className='relative tablet:max-w-64 shrink-0'>
                <DataTableSearchInput {...searchProps} />
              </div>
            )}
            {datePicker && <div className='shrink-0'>{datePicker}</div>}
            {hasFilters ? (
              <ToolbarFiltersRow
                filtersDef={filtersDef}
                filtersOptions={filtersOptions}
                filtersLoading={filtersLoading}
                onChangeFilter={onChangeFilter}
                onSelectedFiltersChange={onSelectedFiltersChange}
                favoriteFilters={favoriteFilters}
                filtersOnSecondRow={false}
                wrapperClassName='flex flex-grow items-center gap-4 overflow-hidden'
                trailing={filtersDropdown}
              />
            ) : (
              filtersDropdown && <div className='flex flex-grow items-center justify-end overflow-hidden'>{filtersDropdown}</div>
            )}
            <ToolbarActions showColumnFilter={showColumnFilter} columns={columns} visibleColumns={visibleColumns} exportDef={exportDef} Export={Export} data={data} table={table} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default DataTableToolbar;
